/**
 * @fileoverview Central route definitions for the application
 * 
 * This file defines all application routes with their metadata including:
 * - Route codes (hierarchical dot notation, e.g., '1', '1.5', '1.5.1')
 * - URL paths and components
 * - Icons, breadcrumbs, and authentication requirements
 * 
 * Route Code Structure:
 * - Root routes use single-digit codes: '1', '2', '3', etc.
 * - Child routes use dot notation: '1.5', '1.5.1', '1.5.2'
 * - Icon inheritance: Child routes without icons inherit from their root (first digit)
 * 
 * Backend Integration:
 * - Backend sends only the route 'code' via menu API
 * - All other metadata (icon, title, path, component) is defined here
 * - Menu mapper (menu.mapper.ts) uses getRouteByCode to enrich menu items
 * - Breadcrumb hook (useBreadcrumb.ts) uses getRouteByPath for navigation
 */

import type { IconScoutName } from '@/components/ui/images/IconScout';
import type { ExternalMenuKind } from '@/api/domains/menu/menu.types';
import type { ComponentType } from 'react';
import type { SectionName } from '@/types/sections';
import { SECTIONS } from '@/types/sections';

// Pages imports
import { AttendanceDashboard, MyAttendance, MyMonthlyAttendance, ShiftChangeRequests, ShiftChangeRequestsAdmin, ManagedAttendance, MyIncidents, ImputarAsistencia, AttendanceConfiguration, HolidayListPage, AttendanceConceptListPage, AttendanceMarksAdmin, MarksManager, RecalculoAsistencia, MonthlyCutsListPage, MonthlyCutCreatePage, MonthlyCutEditPage, MarksReport, EnrollmentReport, IncidentManagementList, AttendanceMonitoringPage } from '@/pages/attendance';
import { AbsenceSummary, AbsenceScopeList, AbsenceApproval } from '@/pages/absence';
import { OvertimeApproval, OvertimeApprovalDetail, OvertimeReport } from '@/pages/overtime';
import { PaymentConceptsPage } from '@/pages/payment';
import { EmployeeList, EmployeeProfile, EmployeeCreate, MyDocuments } from '@/pages/employee';
import { EmployeeContractList, EmployeeContractCreate, EmployeeContractEdit, EmployeeActiveContract } from '@/pages/employee/contracts';
import { CompanyList, CompanyProfile, CompanyCreate } from '@/pages/company';
import { Organization } from '@/pages/organization';
import { Login, ChangePassword, SSOLinkAccount } from '@/pages/account';
import { AuthorizationPage } from '@/pages/authorization';
import { SecurityGroupingPage } from '@/pages/securityGrouping';
import { ScheduleList, ScheduleCreate, ScheduleEdit } from '@/pages/schedule';
import { ShiftList, ShiftCreate, ShiftEdit } from '@/pages/shift';
import { ShiftAssignmentList } from '@/pages/shiftAssignment';
import { FractionalLeaveList } from '@/pages/fractionalLeave';
import { NotFound, Forbidden } from '@/pages/httpStatus';
import { AuditRecordsList } from '@/pages/audit';
import { CheckInLocationPage, GeolocalizationPage } from '@/pages/checkInLocation';
import { AsyncJobListPage } from '@/pages/asyncJob';
import { DeviceList } from '@/pages/device';


/**
 * Route definition interface
 * 
 * @property code - Hierarchical route code (e.g., '1', '1.5.1')
 *   - Root codes are single digits: '1', '2', '3'
 *   - Child codes use dot notation: '1.5', '1.5.1', '1.5.2'
 *   - Icon inheritance follows this hierarchy (first digit = root)
 * @property path - URL path (may include :params)
 * @property component - React component to render
 * @property icon - IconScout icon name (optional, inherits from root if not set)
 * @property breadcrumb - Breadcrumb navigation data
 * @property requiresAuth - Whether route requires authentication
 * @property layout - Layout type ('main' or 'none')
 */
export interface RouteDefinition {
  code?: string;
  path: string;
  component: ComponentType<any>;
  icon?: IconScoutName;
  /**
   * Marks a menu item that opens an external destination instead of navigating
   * internally. The value identifies which external handler to use.
   */
  externalKind?: ExternalMenuKind;
  breadcrumb: {
    title: string;
    items: Array<{ text: string; href?: string }>;
    parentPath?: string;
  };
  requiresAuth?: boolean;
  layout?: 'main' | 'none';
  sections?: SectionName[];
  requiredSection?: SectionName;
}

export const ROUTE_DEFINITIONS: Record<string, RouteDefinition> = {
  // ============================================================================
  // RUTAS SIN AUTENTICACIÓN
  // ============================================================================
  
  INICIAR_SESION: {
    code: 'login',
    path: '/login',
    component: Login,
    breadcrumb: {
      title: 'Iniciar Sesión',
      items: [{ text: 'Login' }],
    },
    requiresAuth: false,
    layout: 'none',
  },

  CAMBIAR_CONTRASENA: {
    code: 'change_password',
    path: '/change_password',
    component: ChangePassword,
    breadcrumb: {
      title: 'Cambiar Contraseña',
      items: [{ text: 'Cambiar Contraseña' }],
    },
    requiresAuth: false,
    layout: 'none',
  },

  VINCULAR_CUENTA_SSO: {
    code: 'sso_link',
    path: '/sso/link-account',
    component: SSOLinkAccount,
    breadcrumb: {
      title: 'Vincular cuenta con Rex+',
      items: [{ text: 'Vincular cuenta' }],
    },
    requiresAuth: true,
    layout: 'none',
  },

  // ============================================================================
  // SECCIONES PRINCIPALES (Padres para herencia de íconos)
  // ============================================================================

  MI_ASISTENCIA_ROOT: {
    code: '1',
    path: '/asistencia-root',
    component: NotFound,
    icon: 'clockThree',
    breadcrumb: {
      title: 'Mi asistencia',
      items: [{ text: 'Mi asistencia' }],
    },
    requiresAuth: true,
  },

  GESTION_ROOT: {
    code: '2',
    path: '/gestion',
    component: NotFound,
    icon: 'cog',
    breadcrumb: {
      title: 'Gestión',
      items: [{ text: 'Gestión' }],
    },
    requiresAuth: true,
  },

  INFORMES_ROOT: {
    code: '3',
    path: '/informes',
    component: NotFound,
    icon: 'chart',
    breadcrumb: {
      title: 'Informes',
      items: [{ text: 'Informes' }],
    },
    requiresAuth: true,
  },

  MASIVO_ROOT: {
    code: '4',
    path: '/masivo',
    component: NotFound,
    icon: 'import',
    breadcrumb: {
      title: 'Masivo',
      items: [{ text: 'Masivo' }],
    },
    requiresAuth: true,
  },

  CONFIGURACION_ROOT: {
    code: '5',
    path: '/configuracion',
    component: NotFound,
    icon: 'setting',
    breadcrumb: {
      title: 'Configuración',
      items: [{ text: 'Configuración' }],
    },
    requiresAuth: true,
  },

  ADMINISTRACION_ROOT: {
    code: '6',
    path: '/administracion',
    component: NotFound,
    icon: 'usersAlt',
    breadcrumb: {
      title: 'Administración',
      items: [{ text: 'Administración' }],
    },
    requiresAuth: true,
  },

  // ============================================================================
  // 1. MI ASISTENCIA (código BD: 1.*)
  // ============================================================================

  // 1.5 - Registro de tiempo
  INICIO: {
    code: '1.5.2',
    path: '/asistencia/mis-registros',
    component: MyAttendance,
    breadcrumb: {
      title: 'Mis registros de asistencia',
      items: [{ text: 'Mi asistencia' }, { text: 'Registros de asistencia' }],
    },
    requiresAuth: true,
  },

  DASHBOARD_ASISTENCIA: {
    code: '1.5.3',
    path: '/',
    component: AttendanceDashboard,
    breadcrumb: {
      title: 'Dashboard Asistencia',
      items: [{ text: 'Mi asistencia' }, { text: 'Dashboard Asistencia' }],
    },
    requiresAuth: true,
  },

  // 1.2 - Gestión
  REGISTRO_INCIDENTE: {
    code: '1.2.2',
    path: '/mi_asistencia/gestion/registro_incidente',
    component: NotFound,
    breadcrumb: {
      title: 'Registro de incidente',
      items: [{ text: 'Mi asistencia' }, { text: 'Gestión' }, { text: 'Registro de incidente' }],
    },
    requiresAuth: true,
  },

  VALIDACION_ASISTENCIA_EMPLEADO: {
    path: '/mi_asistencia/gestion/validacion_asistencia',
    component: NotFound,
    breadcrumb: {
      title: 'Validación de asistencia',
      items: [{ text: 'Mi asistencia' }, { text: 'Gestión' }, { text: 'Validación de asistencia' }],
    },
    requiresAuth: true,
  },

  CAMBIO_TURNO_EMPLEADO: {
    code: '2.3.7',
    path: '/mi_asistencia/gestion/cambio_turno',
    component: ShiftChangeRequests,
    breadcrumb: {
      title: 'Cambio de turno',
      items: [{ text: 'Mi asistencia' }, { text: 'Gestión' }, { text: 'Cambio de turno' }],
    },
    requiresAuth: true,
  },

  // 1.1 - Firmas y documentos
  FIRMAS: {
    code: '2.4.1',
    path: '/mi_asistencia/firmas_documentos/firmas',
    component: NotFound,
    breadcrumb: {
      title: 'Mis firmas',
      items: [{ text: 'Mi asistencia' }, { text: 'Firmas y documentos' }, { text: 'Mis firmas' }],
    },
    requiresAuth: true,
  },

  CARPETA_DIGITAL: {
    code: '2.4.2',
    path: '/mi_asistencia/firmas_documentos/carpeta_digital',
    component: MyDocuments,
    breadcrumb: {
      title: 'Carpeta digital',
      items: [{ text: 'Mi asistencia' }, { text: 'Firmas y documentos' }, { text: 'Carpeta digital' }],
    },
    requiresAuth: true,
  },

  HORAS_COMPENSADAS_EMPLEADO: {
    path: '/mi_asistencia/informes/horas_compensadas',
    component: NotFound,
    breadcrumb: {
      title: 'Horas compensadas',
      items: [{ text: 'Mi asistencia' }, { text: 'Informes' }, { text: 'Horas compensadas' }],
    },
    requiresAuth: true,
  },

  AUSENCIAS_EMPLEADO: {
    path: '/mi_asistencia/informes/ausencias',
    component: NotFound,
    breadcrumb: {
      title: 'Ausencias',
      items: [{ text: 'Mi asistencia' }, { text: 'Informes' }, { text: 'Ausencias' }],
    },
    requiresAuth: true,
  },

  AUSENTISMOS_EMPLEADO: {
    path: '/mi_asistencia/informes/ausentismos',
    component: NotFound,
    breadcrumb: {
      title: 'Ausentismos',
      items: [{ text: 'Mi asistencia' }, { text: 'Informes' }, { text: 'Ausentismos' }],
    },
    requiresAuth: true,
  },

  HORAS_EXTRAS_EMPLEADO: {
    path: '/mi_asistencia/informes/horas_extras',
    component: NotFound,
    breadcrumb: {
      title: 'Horas extras',
      items: [{ text: 'Mi asistencia' }, { text: 'Informes' }, { text: 'Horas extras' }],
    },
    requiresAuth: true,
  },

  // 1.4 - Notificaciones
  NOTIFICACIONES_EMPLEADO: {
    code: '1.4.1',
    path: '/mi_asistencia/notificaciones',
    component: NotFound,
    breadcrumb: {
      title: 'Notificaciones empleado',
      items: [{ text: 'Mi asistencia' }, { text: 'Notificaciones' }],
    },
    requiresAuth: true,
  },

  // ============================================================================
  // 6. ADMINISTRACIÓN (código BD: 6.*)
  // ============================================================================

  // 6.2 - Colaborador
  EMPLEADOS: {
    code: '6.2.3',
    path: '/administracion/colaborador/empleados',
    component: EmployeeList,
    breadcrumb: {
      title: 'Listado colaboradores',
      items: [{ text: 'Administración' }, { text: 'Colaborador' }, { text: 'Listado' }],
    },
    requiresAuth: true,
    sections: [SECTIONS.GENERAL, SECTIONS.CONDICIONES_LABORALES],
  },

  EMPLEADOS_CREAR: {
    path: '/administracion/colaborador/empleados/crear',
    component: EmployeeCreate,
    breadcrumb: {
      title: 'Nuevo colaborador',
      items: [{ text: 'Administración' }, { text: 'Colaborador' }, { text: 'Nuevo colaborador' }],
      parentPath: '/administracion/colaborador/empleados',
    },
    requiresAuth: true,
  },

  EMPLEADOS_PERFIL: {
    path: '/administracion/colaborador/empleados/:employeeId',
    component: EmployeeProfile,
    breadcrumb: {
      title: 'Perfil',
      items: [{ text: 'Administración' }, { text: 'Colaborador' }, { text: 'Perfil' }],
      parentPath: '/administracion/colaborador/empleados',
    },
    requiresAuth: true,
  },

  // Sección Contrato activo del colaborador (ASIS-5422 / ASIS-5431 / ASIS-5433).
  // No es una pantalla propia sino una sección de EMPLEADOS, y por eso no lleva `code`: sin él no
  // puede aparecer en el menú (`getRouteByCode` sólo indexa rutas con código) y sólo se llega desde
  // el CardNav del colaborador. `resolveScreenCode` sube a `parentPath` y resuelve 6.2.3
  // (LISTAR_EMPLEADOS), la misma interfaz que exige el backend, pero el código de pantalla no
  // distingue secciones: `requiredSection` agrega la que el backend valida de verdad.
  EMPLEADOS_CONTRATO_ACTIVO: {
    path: '/administracion/colaborador/empleados/:employeeId/contrato-activo',
    component: EmployeeActiveContract,
    requiredSection: SECTIONS.CONDICIONES_LABORALES,
    breadcrumb: {
      title: 'Contrato activo',
      items: [{ text: 'Administración' }, { text: 'Colaborador' }, { text: 'Contrato activo' }],
      parentPath: '/administracion/colaborador/empleados',
    },
    requiresAuth: true,
  },

  EMPLEADO_CONTRATOS: {
    code: '6.2.2',
    path: '/administracion/colaborador/contratos',
    component: EmployeeContractList,
    breadcrumb: {
      title: 'Contratos',
      items: [{ text: 'Administración' }, { text: 'Colaborador' }, { text: 'Contratos' }],
    },
    requiresAuth: true,
  },

  EMPLEADO_CONTRATOS_CREAR: {
    path: '/administracion/colaborador/:employeeId/contratos/crear',
    component: EmployeeContractCreate,
    breadcrumb: {
      title: 'Nuevo contrato',
      items: [{ text: 'Administración' }, { text: 'Colaborador' }, { text: 'Contratos' }, { text: 'Nuevo' }],
      parentPath: '/administracion/colaborador/contratos',
    },
    requiresAuth: true,
  },

  EMPLEADO_CONTRATOS_EDITAR: {
    path: '/administracion/colaborador/:employeeId/contratos/:contractId',
    component: EmployeeContractEdit,
    breadcrumb: {
      title: 'Editar contrato',
      items: [{ text: 'Administración' }, { text: 'Colaborador' }, { text: 'Contratos' }, { text: 'Editar' }],
      parentPath: '/administracion/colaborador/contratos',
    },
    requiresAuth: true,
  },

  AUSENCIAS_EMPLEADOS: {
    path: '/administracion/colaborador/ausencias',
    component: NotFound,
    breadcrumb: {
      title: 'Ausencias',
      items: [{ text: 'Administración' }, { text: 'Colaborador' }, { text: 'Ausencias' }],
    },
    requiresAuth: true,
  },

  // 6.1 - Ausentismos

  JORNADAS_ESPECIALES: {
    code: '6.1.1',
    path: '/administracion/ausentismos/jornadas_especiales',
    component: NotFound,
    breadcrumb: {
      title: 'Jornadas especiales',
      items: [{ text: 'Administración' }, { text: 'Ausentismos' }, { text: 'Jornadas especiales' }],
    },
    requiresAuth: true,
  },

  PERMISOS_ESPECIALES: {
    path: '/administracion/ausentismos/permisos_especiales',
    component: NotFound,
    breadcrumb: {
      title: 'Permisos especiales',
      items: [{ text: 'Administración' }, { text: 'Ausentismos' }, { text: 'Permisos especiales' }],
    },
    requiresAuth: true,
  },

  VACACIONES: {
    path: '/administracion/ausentismos/vacaciones',
    component: NotFound,
    breadcrumb: {
      title: 'Vacaciones',
      items: [{ text: 'Administración' }, { text: 'Ausentismos' }, { text: 'Vacaciones' }],
    },
    requiresAuth: true,
  },

  LICENCIAS: {
    path: '/administracion/ausentismos/licencias',
    component: NotFound,
    breadcrumb: {
      title: 'Licencias',
      items: [{ text: 'Administración' }, { text: 'Ausentismos' }, { text: 'Licencias' }],
    },
    requiresAuth: true,
  },

  PERMISOS_ADMINISTRATIVOS: {
    path: '/administracion/ausentismos/permisos_administrativos',
    component: NotFound,
    breadcrumb: {
      title: 'Permisos administrativos',
      items: [{ text: 'Administración' }, { text: 'Ausentismos' }, { text: 'Permisos administrativos' }],
    },
    requiresAuth: true,
  },

  // ============================================================================
  // 2. GESTIÓN (código BD: 2.*)
  // ============================================================================

  // 2.3 - Gestionar asistencia
  SINCRONIZAR_AUSENTISMOS: {
    path: '/gestion/gestionar_asistencia/sincronizar_ausentismos',
    component: NotFound,
    breadcrumb: {
      title: 'Sincronizar ausentismos',
      items: [{ text: 'Gestión' }, { text: 'Gestionar asistencia' }, { text: 'Sincronizar ausentismos' }],
    },
    requiresAuth: true,
  },

  RECALCULO_ASISTENCIA: {
    code: '2.3.5',
    path: '/gestion/gestionar_asistencia/recalculo_asistencia',
    component: RecalculoAsistencia,
    breadcrumb: {
      title: 'Recálculo asistencia',
      items: [{ text: 'Gestión' }, { text: 'Gestionar asistencia' }, { text: 'Recálculo asistencia' }],
    },
    requiresAuth: true,
  },

  GESTOR_MARCAS_PERIODO: {
    code: '2.3.3',
    path: '/gestion/gestionar_asistencia/gestor_marcas_periodo',
    component: NotFound,
    breadcrumb: {
      title: 'Gestor de marcas periodo',
      items: [{ text: 'Gestión' }, { text: 'Gestionar asistencia' }, { text: 'Gestor marcas periodo' }],
    },
    requiresAuth: true,
  },

  GESTOR_MARCAS_DIAS: {
    path: '/gestion/gestionar_asistencia/gestor_marcas_dias',
    component: NotFound,
    breadcrumb: {
      title: 'Gestor de marcas días',
      items: [{ text: 'Gestión' }, { text: 'Gestionar asistencia' }, { text: 'Gestor marcas días' }],
    },
    requiresAuth: true,
  },

  // ASIS-5567 (épica ASIS-5566) — Nueva pantalla unificada "Gestor de Marcas" que reemplazará a
  // GESTOR_MARCAS_PERIODO / GESTOR_MARCAS_DIAS (stubs legado arriba, aún sin decisión de retiro —
  // ver plan de ASIS-5567). code '2.3.11' coincide con v2_pfMenuItem.Codigo sembrado por
  // db/liquibase/BnovusCorreos/changelog/migrations/V032__seed_gestor_marcas_menu.sql (ver
  // MarksManagerSections en el backend). path confirmado textual desde la épica:
  // "gestion/gestionarasistencia/gestor_marcas" (sin guión bajo entre "gestionar" y "asistencia"
  // — difiere del patrón de las rutas hermanas, pero es el texto literal del ticket).
  // requiredSection: el permiso de pantalla por sí solo (canView) no distingue la función
  // "General" que valida el backend ([RequireEmployeePermission(GESTOR_MARCAS, GENERAL, READ)])
  // — sin esto, un usuario con el ítem de menú visible pero sin esa función concreta vería el
  // shell completo con tabla vacía en vez de la pantalla de "Acceso denegado" (mismo patrón que
  // EMPLEADOS_CONTRATO_ACTIVO).
  GESTOR_MARCAS: {
    code: '2.3.11',
    path: '/gestion/gestionarasistencia/gestor_marcas',
    component: MarksManager,
    requiredSection: SECTIONS.GENERAL,
    breadcrumb: {
      title: 'Gestor de marcas',
      items: [{ text: 'Gestión' }, { text: 'Gestionar asistencia' }, { text: 'Gestor de marcas' }],
    },
    requiresAuth: true,
  },

  IMPUTAR_ASISTENCIA: {
    code: '2.3.4',
    path: '/gestion/gestionar_asistencia/imputar_asistencia',
    component: ImputarAsistencia,
    breadcrumb: {
      title: 'Imputar asistencia',
      items: [{ text: 'Gestión' }, { text: 'Gestionar asistencia' }, { text: 'Imputar asistencia' }],
    },
    requiresAuth: true,
  },

  VALIDACION_ASISTENCIA: {
    code: '2.3.6',
    path: '/gestion/gestionar_asistencia/validacion_asistencia',
    component: NotFound,
    breadcrumb: {
      title: 'Validación de asistencia',
      items: [{ text: 'Gestión' }, { text: 'Gestionar asistencia' }, { text: 'Validación de asistencia' }],
    },
    requiresAuth: true,
  },

  HABILITAR_DESHABILITAR_MARCAJES: {
    code: '2.3.1',
    path: '/gestion/gestionar_asistencia/habilitar_deshabilitar_marcajes',
    component: AttendanceMarksAdmin,
    breadcrumb: {
      title: 'Habilitar - deshabilitar marcajes',
      items: [{ text: 'Gestión' }, { text: 'Gestionar asistencia' }, { text: 'Habilitar - deshabilitar marcajes' }],
    },
    requiresAuth: true,
  },

  // ASIS-5230 — Listado (company-scoped) de solicitudes de cambio de turno.
  // Menú: Gestión > Gestionar asistencia > Solicitudes cambios de turno (code 2.3.2).
  GESTION_CAMBIO_TURNO: {
    code: '2.3.2',
    path: '/gestion/gestionar_asistencia/solicitudes_cambio_turno',
    component: ShiftChangeRequestsAdmin,
    breadcrumb: {
      title: 'Solicitudes de cambio de turno',
      items: [{ text: 'Gestión' }, { text: 'Gestionar asistencia' }, { text: 'Solicitudes cambios de turno' }],
    },
    requiresAuth: true,
  },

  MIS_INCIDENTES: {
    code: '2.3.8',
    path: '/gestion/gestionar_asistencia/incidentes_de_marcajes',
    component: MyIncidents,
    breadcrumb: {
      title: 'Incidentes de marcajes',
      items: [{ text: 'Gestión' }, { text: 'Gestionar asistencia' }, { text: 'Incidentes de marcajes' }],
    },
    requiresAuth: true,
  },

  // ASIS-5244 — Listado (company-scoped) de incidencias de marcaje con filtros.
  // Épica ASIS-5242. Menú: Gestión › Incidencias de marcaje (code 2.3.10).
  INCIDENCIAS_MARCAJE: {
    code: '2.3.10',
    path: '/gestion/gestionar_asistencia/incidencias-marcaje',
    component: IncidentManagementList,
    breadcrumb: {
      title: 'Incidencias de marcaje',
      items: [{ text: 'Gestión' }, { text: 'Gestionar asistencia' }, { text: 'Incidencias de marcaje' }],
    },
    requiresAuth: true,
  },

  MIS_MARCAS_VALIDAR: {
    code: '2.3.9',
    path: '/gestion/gestionar_asistencia/mis_marcas_validar',
    component: ManagedAttendance,
    breadcrumb: {
      title: 'Mis marcas a validar',
      items: [{ text: 'Gestión' }, { text: 'Gestionar asistencia' }, { text: 'Mis marcas a validar' }],
    },
    requiresAuth: true,
  },

  // 2.1 - Aprobaciones
  COMPENSACION_SEMANAL: {
    code: '2.1.4',
    path: '/gestion/aprobaciones/compensacion_semanal',
    component: NotFound,
    breadcrumb: {
      title: 'Compensación semanal',
      items: [{ text: 'Gestión' }, { text: 'Aprobaciones' }, { text: 'Compensación semanal' }],
    },
    requiresAuth: true,
  },

  APROBAR_HHEE: {
    code: '2.1.3',
    path: '/gestion/aprobaciones/aprobacion_hhee',
    component: OvertimeApproval,
    breadcrumb: {
      title: 'Aprobación HHEE',
      items: [{ text: 'Gestión' }, { text: 'Aprobaciones' }, { text: 'Aprobación HHEE' }],
    },
    requiresAuth: true,
  },

  // ASIS-5355: detalle de solo lectura de horas extras de uno o varios colaboradores
  // seleccionados en APROBAR_HHEE. Recibe `employeeIds` (CSV, incluso para un solo colaborador)
  // y `monthlyCutId` por query string. Sin `code` propio: `resolveScreenCode` sube a `parentPath`
  // y hereda el permiso 2.1.3 de la pantalla padre — sin él, el guard deniega el acceso a todos.
  APROBAR_HHEE_DETALLE: {
    path: '/gestion/aprobaciones/aprobacion_hhee/detalle',
    component: OvertimeApprovalDetail,
    breadcrumb: {
      title: 'Detalle de horas extras',
      items: [
        { text: 'Gestión' },
        { text: 'Aprobaciones' },
        { text: 'Aprobación HHEE' },
        { text: 'Detalle de horas extras' },
      ],
      parentPath: '/gestion/aprobaciones/aprobacion_hhee',
    },
    requiresAuth: true,
  },

  APROBAR_ATRASOS: {
    code: '2.1.1',
    path: '/gestion/aprobaciones/aprobar_atrasos',
    component: NotFound,
    breadcrumb: {
      title: 'Aprobar atrasos',
      items: [{ text: 'Gestión' }, { text: 'Aprobaciones' }, { text: 'Aprobar atrasos' }],
    },
    requiresAuth: true,
  },

  APROBAR_AUSENCIAS: {
    code: '2.1.2',
    path: '/gestion/aprobaciones/aprobar_ausencias',
    component: AbsenceApproval,
    breadcrumb: {
      title: 'Aprobación horas ausentes',
      items: [{ text: 'Gestión' }, { text: 'Aprobaciones' }, { text: 'Aprobación horas ausentes' }],
    },
    requiresAuth: true,
  },

  // 2.2 - Envío
  ENVIAR_AUSENCIAS: {
    path: '/gestion/envio/enviar_ausencias',
    component: NotFound,
    breadcrumb: {
      title: 'Envío de Ausencias',
      items: [{ text: 'Gestión' }, { text: 'Envío' }, { text: 'Envío de Ausencias' }],
    },
    requiresAuth: true,
  },

  // ============================================================================
  // 3. INFORMES (código BD: 3.*)
  // ============================================================================

  // 3.4 - Gestión
  LISTADO_MARCAJES: {
    code: '3.4.4',
    path: '/informes/gestion/marcaje_asistencia',
    component: MarksReport,
    breadcrumb: {
      title: 'Marcajes de asistencia',
      items: [{ text: 'Informes' }, { text: 'Gestión' }, { text: 'Marcaje de asistencia' }],
    },
    requiresAuth: true,
  },

  MONITOREO_ASISTENCIA: {
    code: '3.4.5',
    path: '/informes/gestion/monitoreo_asistencia',
    component: AttendanceMonitoringPage,
    breadcrumb: {
      title: 'Monitoreo Asistencia',
      items: [{ text: 'Informes' }, { text: 'Gestión' }, { text: 'Monitoreo Asistencia' }],
    },
    requiresAuth: true,
  },

  HORAS_EXTRAS: {
    code: '3.4.3',
    path: '/informes/gestion/horas_extras',
    component: NotFound,
    breadcrumb: {
      title: 'Horas extras',
      items: [{ text: 'Informes' }, { text: 'Gestión' }, { text: 'Horas extras' }],
    },
    requiresAuth: true,
  },

  ATRASOS: {
    code: '3.4.1',
    path: '/informes/gestion/atrasos',
    component: NotFound,
    breadcrumb: {
      title: 'Atrasos',
      items: [{ text: 'Informes' }, { text: 'Gestión' }, { text: 'Atrasos' }],
    },
    requiresAuth: true,
  },

  DETALLE_HORARIO: {
    code: '3.4.2',
    path: '/informes/gestion/detalle_horario',
    component: NotFound,
    breadcrumb: {
      title: 'Detalle de horario empleado',
      items: [{ text: 'Informes' }, { text: 'Gestión' }, { text: 'Detalle horario' }],
    },
    requiresAuth: true,
  },

  HORAS_TRABAJADAS: {
    path: '/informes/gestion/horas_trabajadas',
    component: NotFound,
    breadcrumb: {
      title: 'Horas trabajadas',
      items: [{ text: 'Informes' }, { text: 'Gestión' }, { text: 'Horas trabajadas' }],
    },
    requiresAuth: true,
  },

  INFORME_COMPORTAMIENTO: {
    path: '/informes/gestion/informe_comportamiento',
    component: NotFound,
    breadcrumb: {
      title: 'Informe Comportamiento',
      items: [{ text: 'Informes' }, { text: 'Gestión' }, { text: 'Informe Comportamiento' }],
    },
    requiresAuth: true,
  },

  REPORTE_CUMPLIMIENTO: {
    path: '/informes/gestion/reporte_cumplimiento',
    component: NotFound,
    breadcrumb: {
      title: 'Reporte Cumplimiento',
      items: [{ text: 'Informes' }, { text: 'Gestión' }, { text: 'Reporte Cumplimiento' }],
    },
    requiresAuth: true,
  },

  ASISTENCIA_DIARIO: {
    path: '/informes/gestion/asistencia_diario',
    component: NotFound,
    breadcrumb: {
      title: 'Asistencia diario',
      items: [{ text: 'Informes' }, { text: 'Gestión' }, { text: 'Asistencia diario' }],
    },
    requiresAuth: true,
  },

  // 3.1 - Asistencia
  INFORME_ASISTENCIA: {
    code: '3.1.3',
    path: '/informes/asistencia/informe_asistencia',
    component: NotFound,
    breadcrumb: {
      title: 'Informe asistencia',
      items: [{ text: 'Informes' }, { text: 'Asistencia' }, { text: 'Informe asistencia' }],
    },
    requiresAuth: true,
  },

  HORAS_COMPENSADAS: {
    code: '3.1.2',
    path: '/informes/asistencia/horas_compensadas',
    component: NotFound,
    breadcrumb: {
      title: 'Horas compensadas',
      items: [{ text: 'Informes' }, { text: 'Asistencia' }, { text: 'Horas compensadas' }],
    },
    requiresAuth: true,
  },

  INFORME_DETALLE_ASISTENCIA: {
    code: '3.1.4',
    path: '/informes/asistencia/informe_detalle_asistencia',
    component: NotFound,
    breadcrumb: {
      title: 'Informe detalle asistencia',
      items: [{ text: 'Informes' }, { text: 'Asistencia' }, { text: 'Informe detalle' }],
    },
    requiresAuth: true,
  },

  PERMISOS_FRACCION: {
    code: '3.1.7',
    path: '/informes/asistencia/permisos_fraccion',
    component: NotFound,
    breadcrumb: {
      title: 'Permisos Fracción',
      items: [{ text: 'Informes' }, { text: 'Asistencia' }, { text: 'Permisos Fracción' }],
    },
    requiresAuth: true,
  },

  MARCAJES_PENDIENTE_APROBACION: {
    code: '3.1.5',
    path: '/informes/asistencia/marcajes_pendiente_aprobacion',
    component: NotFound,
    breadcrumb: {
      title: 'Marcajes pendiente aprobación',
      items: [{ text: 'Informes' }, { text: 'Asistencia' }, { text: 'Marcajes pendiente' }],
    },
    requiresAuth: true,
  },

  PERMANENCIA: {
    code: '3.1.6',
    path: '/informes/asistencia/permanencia',
    component: NotFound,
    breadcrumb: {
      title: 'Permanencia',
      items: [{ text: 'Informes' }, { text: 'Asistencia' }, { text: 'Permanencia' }],
    },
    requiresAuth: true,
  },

  MI_ASISTENCIA_MENSUAL: {
    code: '3.1.8',
    path: '/informes/asistencia/mi_asistencia_mensual',
    component: MyMonthlyAttendance,
    breadcrumb: {
      title: 'Mi asistencia mensual',
      items: [{ text: 'Informes' }, { text: 'Asistencia' }, { text: 'Mi asistencia mensual' }],
    },
    requiresAuth: true,
  },

  RESUMEN_ASISTENCIA: {
    code: '3.1.9',
    path: '/informes/asistencia/resumen_asistencia',
    component: OvertimeReport,
    breadcrumb: {
      title: 'Resumen asistencia',
      items: [{ text: 'Informes' }, { text: 'Asistencia' }, { text: 'Resumen asistencia' }],
    },
    requiresAuth: true,
  },

  RESUMEN_AUSENTISMOS: {
    code: '3.1.10',
    path: '/informes/asistencia/resumen_ausentismos',
    component: AbsenceSummary,
    breadcrumb: {
      title: 'Resumen ausentismos',
      items: [{ text: 'Informes' }, { text: 'Asistencia' }, { text: 'Resumen ausentismos' }],
    },
    requiresAuth: true,
  },

  // ASIS-5287 — Listado paginado de ausentismos por alcance (vista de gestión multiempleado).
  // code pendiente de confirmar con el equipo de backend/menú.
  GESTION_AUSENTISMOS: {
    code: '6.1.2',
    path: '/administracion/ausentismos/gestion_ausentismos',
    component: AbsenceScopeList,
    breadcrumb: {
      title: 'Gestión de ausentismos',
      items: [{ text: 'Administración' }, { text: 'Ausentismos' }, { text: 'Gestión de ausentismos' }],
    },
    requiresAuth: true,
  },

  // 3.5 - Inconsistencias
  MARCAJES_INCONSISTENTES: {
    code: '3.5.2',
    path: '/informes/inconsistencias/marcajes_inconsistentes',
    component: NotFound,
    breadcrumb: {
      title: 'Marcajes inconsistentes',
      items: [{ text: 'Informes' }, { text: 'Inconsistencias' }, { text: 'Marcajes inconsistentes' }],
    },
    requiresAuth: true,
  },

  MARCAJES_FALLIDOS: {
    code: '3.5.1',
    path: '/informes/inconsistencias/marcajes_fallidos',
    component: NotFound,
    breadcrumb: {
      title: 'Marcajes fallidos',
      items: [{ text: 'Informes' }, { text: 'Inconsistencias' }, { text: 'Marcajes fallidos' }],
    },
    requiresAuth: true,
  },

  INCONSISTENCIAS: {
    path: '/informes/inconsistencias/inconsistencias',
    component: NotFound,
    breadcrumb: {
      title: 'Inconsistencias',
      items: [{ text: 'Informes' }, { text: 'Inconsistencias' }],
    },
    requiresAuth: true,
  },

  // 3.3 - Dispositivos
  AGRUPACION_RELOJ: {
    code: '3.3.1',
    path: '/informes/dispositivos/agrupacion_reloj',
    component: NotFound,
    breadcrumb: {
      title: 'Agrupación reloj control',
      items: [{ text: 'Informes' }, { text: 'Dispositivos' }, { text: 'Agrupación reloj' }],
    },
    requiresAuth: true,
  },

  ENROLAMIENTOS: {
    code: '3.3.2',
    path: '/informes/dispositivos/enrolamientos',
    component: EnrollmentReport,
    breadcrumb: {
      title: 'Informe de enrolamiento',
      items: [{ text: 'Informes' }, { text: 'Dispositivos' }, { text: 'Enrolamientos' }],
    },
    requiresAuth: true,
  },

  MONITOREO_DISPOSITIVOS: {
    code: '3.3.3',
    path: '/informes/dispositivos/monitoreo_dispositivos',
    component: NotFound,
    externalKind: 'deviceMonitor',
    breadcrumb: {
      title: 'Monitoreo Dispositivos',
      items: [{ text: 'Informes' }, { text: 'Dispositivos' }, { text: 'Monitoreo' }],
    },
    requiresAuth: true,
  },

  // ASIS-5322 — Resumen de estado de dispositivos
  DISPOSITIVOS_MARCAJE: {
    code: '3.3.4',
    path: '/informes/dispositivos/dispositivos_marcaje',
    component: DeviceList,
    breadcrumb: {
      title: 'Dispositivos de marcaje',
      items: [{ text: 'Informes' }, { text: 'Dispositivos' }, { text: 'Dispositivos de marcaje' }],
    },
    requiresAuth: true,
  },

  // 3.6 - Pagos
  PAYMENT_CONCEPTS: {
    code: '3.6.1',
    path: '/informes/pagos/conceptos_pago',
    component: PaymentConceptsPage,
    breadcrumb: {
      title: 'Conceptos de pago',
      items: [{ text: 'Informes' }, { text: 'Pagos' }, { text: 'Conceptos de pago' }],
    },
    requiresAuth: true,
  },

  COMPARATIVO_HHEE_ATRASOS: {
    path: '/informes/pagos/comparativo_hhee_atrasos',
    component: NotFound,
    breadcrumb: {
      title: 'Comparativo HHEE y atrasos',
      items: [{ text: 'Informes' }, { text: 'Pagos' }, { text: 'Comparativo HHEE' }],
    },
    requiresAuth: true,
  },

  ASISTENCIA_REMUNERACIONES: {
    path: '/informes/pagos/asistencia_remuneraciones',
    component: NotFound,
    breadcrumb: {
      title: 'Asistencia para remuneraciones',
      items: [{ text: 'Informes' }, { text: 'Pagos' }, { text: 'Asistencia remuneraciones' }],
    },
    requiresAuth: true,
  },

  // 3.2 - Configuraciones
  PARAMETRIA: {
    code: '3.2.1',
    path: '/informes/configuraciones/parametria',
    component: NotFound,
    breadcrumb: {
      title: 'Parametría',
      items: [{ text: 'Informes' }, { text: 'Configuraciones' }, { text: 'Parametría' }],
    },
    requiresAuth: true,
  },

  // 3.7 - Reportes DT
  ASISTENCIA_DT: {
    code: '3.7.1',
    path: '/informes/reportes-dt/asistencia',
    component: NotFound,
    breadcrumb: {
      title: 'Asistencia',
      items: [{ text: 'Informes' }, { text: 'Reportes DT' }, { text: 'Asistencia' }],
    },
    requiresAuth: true,
  },

  REPORTE_JORNADA_DT: {
    path: '/informes/reportes_dt/reporte_jornada',
    component: NotFound,
    breadcrumb: {
      title: 'Reporte de Jornada',
      items: [{ text: 'Informes' }, { text: 'Reportes DT' }, { text: 'Reporte Jornada' }],
    },
    requiresAuth: true,
  },

  DOMINGOS_FESTIVOS_DT: {
    path: '/informes/reportes_dt/domingos_festivos',
    component: NotFound,
    breadcrumb: {
      title: 'Reportes Domingos y Festivos',
      items: [{ text: 'Informes' }, { text: 'Reportes DT' }, { text: 'Domingos y Festivos' }],
    },
    requiresAuth: true,
  },

  TURNOS_MODIFICADOS_DT: {
    path: '/informes/reportes_dt/turnos_modificados',
    component: NotFound,
    breadcrumb: {
      title: 'Reportes Turnos Modificados',
      items: [{ text: 'Informes' }, { text: 'Reportes DT' }, { text: 'Turnos Modificados' }],
    },
    requiresAuth: true,
  },

  VERIFICAR_MARCAJE: {
    code: '3.7.2',
    path: '/informes/reportes_dt/verificar_marcaje',
    component: NotFound,
    breadcrumb: {
      title: 'Verificar marcaje',
      items: [{ text: 'Informes' }, { text: 'Reportes DT' }, { text: 'Verificar marcaje' }],
    },
    requiresAuth: true,
  },

  // ============================================================================
  // 4. MASIVO (código BD: 4.*)
  // ============================================================================

  // 4.1 - Dotación
  EMPLEADOS_MASIVO: {
    path: '/masivo/dotacion/empleados',
    component: NotFound,
    breadcrumb: {
      title: 'Empleados',
      items: [{ text: 'Masivo' }, { text: 'Dotación' }, { text: 'Empleados' }],
    },
    requiresAuth: true,
  },

  HORARIO_CREAR: {
    path: '/Configuracion/Horariosyturnos/horarios/nuevo',
    component: ScheduleCreate,
    breadcrumb: {
      title: 'Nuevo: Horario',
      items: [{ text: 'Horarios' }, { text: 'Gestión de Horarios' }, { text: 'Nuevo: Horario' }],
      parentPath: '/Configuracion/Horariosyturnos/horarios',
    },
    requiresAuth: true,
  },

  HORARIO_EDITAR: {
    path: '/Configuracion/Horariosyturnos/horarios/:id/editar',
    component: ScheduleEdit,
    breadcrumb: {
      title: 'Editar: Horario',
      items: [{ text: 'Horarios' }, { text: 'Gestión de Horarios' }, { text: 'Editar: Horario' }],
      parentPath: '/Configuracion/Horariosyturnos/horarios',
    },
    requiresAuth: true,
  },

  // 4.2 - Horarios y turnos (masivo)
  HORARIOS_MASIVO: {
    code: '4.2.3',
    path: '/masivo/horarios_y_turnos/horarios',
    component: NotFound,
    breadcrumb: {
      title: 'Horarios',
      items: [{ text: 'Masivo' }, { text: 'Horarios y turnos' }, { text: 'Horarios' }],
    },
    requiresAuth: true,
  },

  UBICACIONES_MASIVO: {
    code: '4.2.4',
    path: '/masivo/horarios_y_turnos/ubicaciones',
    component: NotFound,
    breadcrumb: {
      title: 'Ubicaciones',
      items: [{ text: 'Masivo' }, { text: 'Horarios y turnos' }, { text: 'Ubicaciones' }],
    },
    requiresAuth: true,
  },

  HORARIOS_FLEXIBLES_MASIVO: {
    code: '4.2.1',
    path: '/masivo/horarios_y_turnos/horarios_flexibles',
    component: NotFound,
    breadcrumb: {
      title: 'Horario Flexible',
      items: [{ text: 'Masivo' }, { text: 'Horarios y turnos' }, { text: 'Horario Flexible' }],
    },
    requiresAuth: true,
  },

  HORARIO_FLEXIBLE_ASIGNACION_MASIVO: {
    code: '4.2.2',
    path: '/masivo/horarios_y_turnos/horario_flexible_asignacion',
    component: NotFound,
    breadcrumb: {
      title: 'Horario Flexible asignación',
      items: [{ text: 'Masivo' }, { text: 'Horarios y turnos' }, { text: 'Horario Flexible asignación' }],
    },
    requiresAuth: true,
  },

  // ============================================================================
  // 5. CONFIGURACIÓN (código BD: 5.*)
  // ============================================================================

  // 5.3 - Parámetros
  EMPRESAS: {
    code: '5.3.3',
    path: '/configuracion/parametros/empresas',
    component: CompanyList,
    breadcrumb: {
      title: 'Listado empresas',
      items: [{ text: 'Configuración' }, { text: 'Parámetros' }, { text: 'Empresas' }],
    },
    requiresAuth: true,
  },

  EMPRESAS_CREAR: {
    path: '/configuracion/parametros/empresas/crear',
    component: CompanyCreate,
    breadcrumb: {
      title: 'Nueva empresa',
      items: [{ text: 'Configuración' }, { text: 'Parámetros' }, { text: 'Nueva empresa' }],
      parentPath: '/configuracion/parametros/empresas',
    },
    requiresAuth: true,
  },

  EMPRESAS_PERFIL: {
    path: '/configuracion/parametros/empresas/:companyId',
    component: CompanyProfile,
    breadcrumb: {
      title: 'Perfil',
      items: [{ text: 'Configuración' }, { text: 'Parámetros' }, { text: 'Perfil' }],
      parentPath: '/configuracion/parametros/empresas',
    },
    requiresAuth: true,
  },

  PERFILES_USUARIOS: {
    code: '5.3.2',
    path: '/configuracion/parametros/perfiles_usuarios',
    component: AuthorizationPage,
    breadcrumb: {
      title: 'Perfiles de usuarios',
      items: [{ text: 'Configuración' }, { text: 'Parámetros' }, { text: 'Perfiles usuarios' }],
    },
    requiresAuth: true,
    sections: [SECTIONS.PERMISOS_APP_MOVIL],
  },

  AGRUPACIONES: {
    code: '5.3.1',
    path: '/configuracion/parametros/agrupaciones',
    component: SecurityGroupingPage,
    breadcrumb: {
      title: 'Agrupaciones de Seguridad',
      items: [{ text: 'Configuración' }, { text: 'Parámetros' }, { text: 'Agrupaciones de Seguridad' }],
    },
    requiresAuth: true,
  },

  SINCRONIZACION_REX: {
    path: '/configuracion/parametros/sincronizacion_rex',
    component: NotFound,
    breadcrumb: {
      title: 'Sincronización rex+',
      items: [{ text: 'Configuración' }, { text: 'Parámetros' }, { text: 'Sincronización rex+' }],
    },
    requiresAuth: true,
  },

  ORGANIZACION: {
    code: '5.3.4',
    path: '/configuracion/parametros/organizacion',
    component: Organization,
    breadcrumb: {
      title: 'Organización',
      items: [{ text: 'Configuración' }, { text: 'Parámetros' }, { text: 'Organización' }],
    },
    requiresAuth: true,
    sections: [SECTIONS.GENERAL, SECTIONS.AREAS, SECTIONS.CARGOS, SECTIONS.CENTROS_COSTO],
  },

  // 5.1 - Config.asistencia
  CONFIGURACION_INICIAL: {
    code: '5.1.2',
    path: '/configuracion/config_asistencia/configuracion_inicial',
    component: AttendanceConfiguration,
    breadcrumb: {
      title: 'Configuración inicial',
      items: [{ text: 'Configuración' }, { text: 'Config.asistencia' }, { text: 'Configuración inicial' }],
    },
    requiresAuth: true,
  },

  UBICACIONES_GEOLOCALIZACION: {
    path: '/configuracion/config_asistencia/ubicaciones/:locationId/geolocalizacion',
    component: GeolocalizationPage,
    breadcrumb: {
      title: 'Geo-localización',
      items: [{ text: 'Configuración' }, { text: 'Config.asistencia' }, { text: 'Ubicaciones' }, { text: 'Geo-localización' }],
      parentPath: '/configuracion/config_asistencia/ubicaciones',
    },
    requiresAuth: true,
  },

  UBICACIONES: {
    code: '5.1.6',
    path: '/configuracion/config_asistencia/ubicaciones',
    component: CheckInLocationPage,
    breadcrumb: {
      title: 'Ubicaciones',
      items: [{ text: 'Configuración' }, { text: 'Config.asistencia' }, { text: 'Ubicaciones' }],
    },
    requiresAuth: true,
  },

  LISTA_PERMISOS_FRACCION: {
    code: '5.1.5',
    path: '/configuracion/config_asistencia/lista_permisos_fraccion',
    component: FractionalLeaveList,
    breadcrumb: {
      title: 'Permisos fracción',
      items: [{ text: 'Configuración' }, { text: 'Config.asistencia' }, { text: 'Permisos fracción' }],
    },
    requiresAuth: true,
  },

  CONCEPTOS_ASISTENCIA: {
    code: '5.1.7',
    path: '/configuracion/config_asistencia/conceptos_asistencia',
    component: AttendanceConceptListPage,
    breadcrumb: {
      title: 'Conceptos asistencia',
      items: [{ text: 'Configuración' }, { text: 'Config.asistencia' }, { text: 'Conceptos asistencia' }],
    },
    requiresAuth: true,
  },

  CORTE_MENSUAL: {
    code: '5.1.3',
    path: '/configuracion/config_asistencia/corte_mensual',
    component: MonthlyCutsListPage,
    breadcrumb: {
      title: 'Corte mensual',
      items: [{ text: 'Configuración' }, { text: 'Config.asistencia' }, { text: 'Cortes mensuales' }],
    },
    requiresAuth: true,
  },

  CORTE_MENSUAL_CREAR: {
    // Sin code propio: resolveScreenCode sigue parentPath y hereda el permiso del listado.
    path: '/configuracion/config_asistencia/corte_mensual/crear',
    component: MonthlyCutCreatePage,
    breadcrumb: {
      title: 'Nuevo: Corte',
      items: [
        { text: 'Configuración' },
        { text: 'Config.asistencia' },
        { text: 'Cortes mensuales' },
        { text: 'Nuevo' },
      ],
      parentPath: '/configuracion/config_asistencia/corte_mensual',
    },
    requiresAuth: true,
  },

  CORTE_MENSUAL_EDITAR: {
    // Sin code propio: resolveScreenCode sigue parentPath y hereda el permiso del listado.
    path: '/configuracion/config_asistencia/corte_mensual/editar/:id',
    component: MonthlyCutEditPage,
    breadcrumb: {
      title: 'Editar: Corte',
      items: [
        { text: 'Configuración' },
        { text: 'Config.asistencia' },
        { text: 'Cortes mensuales' },
        { text: 'Editar' },
      ],
      parentPath: '/configuracion/config_asistencia/corte_mensual',
    },
    requiresAuth: true,
  },

  FESTIVOS_INHABILES: {
    code: '5.1.8',
    path: '/configuracion/config_asistencia/festivos_inhabiles',
    component: HolidayListPage,
    breadcrumb: {
      title: 'Festivos e inhábiles',
      items: [{ text: 'Configuración' }, { text: 'Config.asistencia' }, { text: 'Festivos e inhábiles' }],
    },
    requiresAuth: true,
  },

  DISPOSITIVO_CONTROL: {
    code: '5.1.4',
    path: '/configuracion/config_asistencia/dispositivo_control',
    component: NotFound,
    breadcrumb: {
      title: 'Dispositivo control',
      items: [{ text: 'Configuración' }, { text: 'Config.asistencia' }, { text: 'Dispositivo control' }],
    },
    requiresAuth: true,
  },

  BLOQUEO_GRUPOS: {
    code: '5.1.1',
    path: '/configuracion/config_asistencia/bloqueo_grupos',
    component: NotFound,
    breadcrumb: {
      title: 'Bloqueo por grupos',
      items: [{ text: 'Configuración' }, { text: 'Config.asistencia' }, { text: 'Bloqueo por grupos' }],
    },
    requiresAuth: true,
  },

  // 5.2 - Horarios y turnos
  EVENTOS: {
    code: '5.2.3',
    path: '/configuracion/horarios_turnos/eventos',
    component: NotFound,
    breadcrumb: {
      title: 'Eventos',
      items: [{ text: 'Configuración' }, { text: 'Horarios y turnos' }, { text: 'Eventos' }],
    },
    requiresAuth: true,
  },

  HORARIOS: {
    code: '5.2.4',
    path: '/Configuracion/Horariosyturnos/horarios',
    component: ScheduleList,
    icon: 'clockThree',
    breadcrumb: {
      title: 'Horarios',
      items: [{ text: 'Configuración' }, { text: 'Horarios y turnos' }, { text: 'Horarios' }],
    },
    requiresAuth: true,
  },

  TURNOS_ROTATIVOS_FIJOS: {
    code: '5.2.5',
    path: '/configuracion/horarios_turnos/turnos_y_jornadas',
    component: ShiftList,
    breadcrumb: {
      title: 'Turnos y Jornadas',
      items: [{ text: 'Configuración' }, { text: 'Horarios y turnos' }, { text: 'Turnos y Jornadas' }],
    },
    requiresAuth: true,
  },

  TURNO_CREAR: {
    path: '/configuracion/horarios_turnos/turnos_y_jornadas/nuevo',
    component: ShiftCreate,
    breadcrumb: {
      title: 'Nuevo turno',
      items: [{ text: 'Configuración' }, { text: 'Horarios y turnos' }, { text: 'Turnos y Jornadas' }, { text: 'Nuevo turno' }],
      parentPath: '/configuracion/horarios_turnos/turnos_y_jornadas',
    },
    requiresAuth: true,
  },

  TURNO_EDITAR: {
    path: '/configuracion/horarios_turnos/turnos_y_jornadas/:id/editar',
    component: ShiftEdit,
    breadcrumb: {
      title: 'Editar turno',
      items: [{ text: 'Configuración' }, { text: 'Horarios y turnos' }, { text: 'Turnos y Jornadas' }, { text: 'Editar turno' }],
      parentPath: '/configuracion/horarios_turnos/turnos_y_jornadas',
    },
    requiresAuth: true,
  },

  ASIGNACIONES_TURNOS: {
    code: '5.2.1',
    path: '/configuracion/horarios_turnos/asignaciones_turnos',
    component: ShiftAssignmentList,
    breadcrumb: {
      title: 'Asignación de turnos',
      items: [{ text: 'Configuración' }, { text: 'Horarios y turnos' }, { text: 'Asignación de turnos' }],
    },
    requiresAuth: true,
  },

  CALENDARIZACION: {
    code: '5.2.2',
    path: '/configuracion/horarios_turnos/calendarizacion',
    component: NotFound,
    breadcrumb: {
      title: 'Calendarización',
      items: [{ text: 'Configuración' }, { text: 'Horarios y turnos' }, { text: 'Calendarización' }],
    },
    requiresAuth: true,
  },

  HORARIO_FLEXIBLE: {
    path: '/configuracion/horarios_turnos/horario_flexible',
    component: NotFound,
    breadcrumb: {
      title: 'Horario flexible',
      items: [{ text: 'Configuración' }, { text: 'Horarios y turnos' }, { text: 'Horario flexible' }],
    },
    requiresAuth: true,
  },

  CALENDARIO_TURNOS: {
    path: '/configuracion/horarios_turnos/calendario_turnos',
    component: NotFound,
    breadcrumb: {
      title: 'Calendario de turnos',
      items: [{ text: 'Configuración' }, { text: 'Horarios y turnos' }, { text: 'Calendario turnos' }],
    },
    requiresAuth: true,
  },
  
  AUDITORIA: {
    code: '5.4.1',
    path: '/configuracion/registros/auditoria',
    component: AuditRecordsList,
    breadcrumb: {
      title: 'Registros auditoría',
      items: [{ text: 'Auditoría' }, { text: 'Registros' }],
    },
    requiresAuth: true,
  },

  PROCESOS_ASINCRONOS: {
    code: '5.4.2',
    path: '/configuracion/registros/procesos_asincronos',
    component: AsyncJobListPage,
    icon: 'setting',
    breadcrumb: {
      title: 'Procesos asíncronos',
      items: [{ text: 'Configuración' }, { text: 'Registros' }, { text: 'Procesos asíncronos' }],
    },
    requiresAuth: true,
  },

  // ============================================================================
  // 403
  // ============================================================================

  ACCESO_DENEGADO: {
    code: 'forbidden',
    path: '/acceso-denegado',
    component: Forbidden,
    breadcrumb: {
      title: 'Acceso Denegado',
      items: [{ text: '403' }],
    },
    requiresAuth: true,
  },

  // ============================================================================
  // 404
  // ============================================================================
  
  PAGINA_NO_ENCONTRADA: {
    code: 'not_found',
    path: '*',
    component: NotFound,
    breadcrumb: {
      title: 'Página No Encontrada',
      items: [{ text: '404' }],
    },
    requiresAuth: false,
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Cache for route lookups by code (Map for O(1) performance)
 * Built once on module load to avoid O(n) searches with 100+ routes
 * Only includes routes that have a code defined (menu items)
 */
const routesByCode = new Map<string, RouteDefinition>(
  Object.values(ROUTE_DEFINITIONS)
    .filter((route) => route.code)
    .map((route) => [route.code!, route]),
);

export const getRouteByCode = (
  code: string,
): RouteDefinition | undefined => routesByCode.get(code);

export const getRouteByPath = (
  path: string,
): RouteDefinition | undefined =>
  Object.values(ROUTE_DEFINITIONS).find((route) => {
    if (route.path.includes(':')) {
      const pattern = new RegExp(
        '^' + route.path.replace(/:[^/]+/g, '[^/]+') + '$',
      );
      return pattern.test(path);
    }
    return route.path === path;
  });

export const getAuthenticatedRoutes = (): RouteDefinition[] =>
  Object.values(ROUTE_DEFINITIONS).filter((route) => route.requiresAuth);

export const getPublicRoutes = (): RouteDefinition[] =>
  Object.values(ROUTE_DEFINITIONS).filter((route) => !route.requiresAuth);

export const getRoutesByLayout = (
  layout: 'main' | 'none',
): RouteDefinition[] => Object.values(ROUTE_DEFINITIONS).filter(
  (route) => route.layout === layout,
);

/**
 * Composite `screenCode` values (`usePaginatedList`) for screens that host more than one
 * independent list as tabs of the same route code — a sub-key disambiguates each tab's
 * persisted preferences. Single source of truth: a typo here is a compile error instead of a
 * silently distinct preferences bucket.
 */
export const SCREEN_PREFERENCE_CODES = {
  ORGANIZACION_AREAS: `${ROUTE_DEFINITIONS.ORGANIZACION.code}.areas`,
  ORGANIZACION_POSITIONS: `${ROUTE_DEFINITIONS.ORGANIZACION.code}.positions`,
  ORGANIZACION_COST_CENTERS: `${ROUTE_DEFINITIONS.ORGANIZACION.code}.costCenters`,
  FESTIVOS_INHABILES_BY_TYPE: `${ROUTE_DEFINITIONS.FESTIVOS_INHABILES.code}.byType`,
  FESTIVOS_INHABILES_BY_REGION: `${ROUTE_DEFINITIONS.FESTIVOS_INHABILES.code}.byRegion`,
  UBICACIONES_LOCATIONS: `${ROUTE_DEFINITIONS.UBICACIONES.code}.locations`,
  UBICACIONES_ASSIGNED: `${ROUTE_DEFINITIONS.UBICACIONES.code}.assigned`,
  AGRUPACIONES_GROUPINGS: `${ROUTE_DEFINITIONS.AGRUPACIONES.code}.groupings`,
  AGRUPACIONES_ASSIGNED_EMPLOYEES: `${ROUTE_DEFINITIONS.AGRUPACIONES.code}.assignedEmployees`,
  TURNOS_ROTATIVOS_FIJOS_SHIFTS: `${ROUTE_DEFINITIONS.TURNOS_ROTATIVOS_FIJOS.code}.shifts`,
  TURNOS_ROTATIVOS_FIJOS_WORKING_DAYS: `${ROUTE_DEFINITIONS.TURNOS_ROTATIVOS_FIJOS.code}.workingDays`,
} as const;
